import type { CompositionRow } from "@shared/utils/lottiePlaygroundModel";
import {
  buildCompositionRows,
  getAtPath,
  getLayerTy,
  getTextDocumentK,
  hexToRgb01,
  listLayerTransformKeys,
  type PathSeg,
  parsePathKey,
  pathKey,
  readFillRgb01,
  readOpacityChannel,
  readStrokeWidth,
  readTextFillRgb01,
  rgb01ToHex,
  setAtPath,
  setLayerTextFillColour,
  staticColourProperty,
  staticOpacityProperty,
  staticStrokeWidth,
  summariseAnimatedProperty,
} from "@shared/utils/lottiePlaygroundModel";
import { useCallback, useEffect, useMemo, useState } from "react";

export interface UseAnimationInspectorOptions {
  animationData: unknown;
  selectedPathKey: string | null;
  onCommitAnimation: (nextRoot: unknown) => void;
}

export interface KeyframeTargetInfo {
  label: string;
  prop: unknown;
}

export function useAnimationInspector({
  animationData,
  selectedPathKey,
  onCommitAnimation,
}: UseAnimationInspectorOptions) {
  const rows = useMemo(() => buildCompositionRows(animationData), [animationData]);

  const selectedPath = useMemo(() => {
    if (!selectedPathKey) {
      return null;
    }
    return parsePathKey(selectedPathKey);
  }, [selectedPathKey]);

  const selectedRow = useMemo(() => {
    if (!selectedPath) {
      return null;
    }
    const want = pathKey(selectedPath);
    return rows.find((r) => pathKey(r.path) === want) ?? null;
  }, [rows, selectedPath]);

  const node = selectedPath ? getAtPath(animationData, selectedPath) : undefined;

  const [layerKsKey, setLayerKsKey] = useState("p");
  const [channelInspect, setChannelInspect] = useState<string>("c");
  const [layerKeyframeSource, setLayerKeyframeSource] = useState<"transform" | "textDoc">("transform");

  const layerKs = useMemo(() => {
    if (!selectedRow || selectedRow.kind !== "layer") {
      return null;
    }
    const layer = getAtPath(animationData, selectedRow.path);
    if (!layer || typeof layer !== "object") {
      return null;
    }
    return (layer as { ks?: unknown }).ks;
  }, [animationData, selectedRow]);

  const layerKsOptions = useMemo(() => listLayerTransformKeys(layerKs), [layerKs]);

  useEffect(() => {
    if (layerKsOptions.length && !layerKsOptions.some((x) => x.key === layerKsKey)) {
      setLayerKsKey(layerKsOptions[0]?.key ?? "p");
    }
  }, [layerKsKey, layerKsOptions]);

  const layerAnimatedProp = useMemo(() => {
    if (!layerKs || typeof layerKs !== "object") {
      return undefined;
    }
    return (layerKs as Record<string, unknown>)[layerKsKey];
  }, [layerKs, layerKsKey]);

  const selectedLayerObject = useMemo(() => {
    if (!selectedRow || selectedRow.kind !== "layer") {
      return undefined;
    }
    const layer = getAtPath(animationData, selectedRow.path);
    if (!layer || typeof layer !== "object") {
      return undefined;
    }
    return layer;
  }, [animationData, selectedRow]);

  const isTextLayer = useMemo(() => getLayerTy(selectedLayerObject) === 5, [selectedLayerObject]);

  const textFillRgb = useMemo(
    () => (isTextLayer ? readTextFillRgb01(selectedLayerObject) : null),
    [isTextLayer, selectedLayerObject]
  );

  const textDocumentK = useMemo(
    () => (isTextLayer ? getTextDocumentK(selectedLayerObject) : undefined),
    [isTextLayer, selectedLayerObject]
  );

  const handleTextFillCommit = useCallback(
    (rgb: [number, number, number]) => {
      if (!selectedRow || selectedRow.kind !== "layer") {
        return;
      }
      const idx = selectedRow.path[1];
      if (typeof idx !== "number") {
        return;
      }
      onCommitAnimation(setLayerTextFillColour(animationData, idx, rgb));
    },
    [animationData, onCommitAnimation, selectedRow]
  );

  const paths = useMemo(() => computeShapeStrokePaths(selectedRow), [selectedRow]);

  const fillColourObj = paths.fillColour ? getAtPath(animationData, paths.fillColour) : undefined;
  const fillOpacityObj = paths.fillOpacity ? getAtPath(animationData, paths.fillOpacity) : undefined;
  const strokeColourObj = paths.strokeColour ? getAtPath(animationData, paths.strokeColour) : undefined;
  const strokeWidthObj = paths.strokeWidth ? getAtPath(animationData, paths.strokeWidth) : undefined;
  const strokeOpacityObj = paths.strokeOpacity ? getAtPath(animationData, paths.strokeOpacity) : undefined;

  const fillRgb = useMemo(() => readFillRgb01(fillColourObj), [fillColourObj]);
  const strokeRgb = useMemo(() => readFillRgb01(strokeColourObj), [strokeColourObj]);

  const fillOpacityStatic = readOpacityChannel(fillOpacityObj);
  const strokeOpacityStatic = readOpacityChannel(strokeOpacityObj);
  const strokeWidthStatic = readStrokeWidth(strokeWidthObj);

  const commitColour = useCallback(
    (path: PathSeg[] | null, rgb: [number, number, number]) => {
      if (!path) {
        return;
      }
      onCommitAnimation(setAtPath(animationData, path, staticColourProperty(rgb)));
    },
    [animationData, onCommitAnimation]
  );

  const handleFillOpacityCommit = useCallback(
    (v: number) => {
      if (!paths.fillOpacity) {
        return;
      }
      onCommitAnimation(setAtPath(animationData, paths.fillOpacity, staticOpacityProperty(v)));
    },
    [animationData, onCommitAnimation, paths.fillOpacity]
  );

  const handleStrokeWidthCommit = useCallback(
    (w: number) => {
      if (!paths.strokeWidth) {
        return;
      }
      onCommitAnimation(setAtPath(animationData, paths.strokeWidth, staticStrokeWidth(w)));
    },
    [animationData, onCommitAnimation, paths.strokeWidth]
  );

  const handleStrokeOpacityCommit = useCallback(
    (v: number) => {
      if (!paths.strokeOpacity) {
        return;
      }
      onCommitAnimation(setAtPath(animationData, paths.strokeOpacity, staticOpacityProperty(v)));
    },
    [animationData, onCommitAnimation, paths.strokeOpacity]
  );

  const keyframeTarget = useMemo(
    (): KeyframeTargetInfo | null =>
      buildKeyframeTarget({
        channelInspect,
        fillColourObj,
        fillOpacityObj,
        layerAnimatedProp,
        layerKeyframeSource,
        layerKsKey,
        layerKsOptions,
        selectedRow,
        strokeColourObj,
        strokeOpacityObj,
        strokeWidthObj,
        textDocumentK,
      }),
    [
      channelInspect,
      fillColourObj,
      fillOpacityObj,
      layerAnimatedProp,
      layerKeyframeSource,
      layerKsKey,
      layerKsOptions,
      selectedRow,
      strokeColourObj,
      strokeOpacityObj,
      strokeWidthObj,
      textDocumentK,
    ]
  );

  const keyframes = useMemo(() => summariseAnimatedProperty(keyframeTarget?.prop), [keyframeTarget]);

  return {
    channelInspect,
    fillColourObj,
    fillOpacityObj,
    fillOpacityStatic,
    fillRgb,
    handleFillOpacityCommit,
    handleStrokeOpacityCommit,
    handleStrokeWidthCommit,
    keyframeTarget,
    keyframes,
    layerAnimatedProp,
    layerKsKey,
    layerKsOptions,
    node,
    selectedRow,
    setChannelInspect,
    setLayerKsKey,
    strokeColourObj,
    strokeOpacityObj,
    strokeOpacityStatic,
    strokeRgb,
    strokeWidthObj,
    strokeWidthStatic,
    commitColour,
    hexToRgb01,
    rgb01ToHex,
    paths,
    isTextLayer,
    textFillRgb,
    handleTextFillCommit,
    layerKeyframeSource,
    setLayerKeyframeSource,
  };
}

function computeShapeStrokePaths(selectedRow: CompositionRow | null): {
  fillColour: PathSeg[] | null;
  fillOpacity: PathSeg[] | null;
  strokeColour: PathSeg[] | null;
  strokeWidth: PathSeg[] | null;
  strokeOpacity: PathSeg[] | null;
} {
  if (!selectedRow) {
    return {
      fillColour: null,
      fillOpacity: null,
      strokeColour: null,
      strokeWidth: null,
      strokeOpacity: null,
    };
  }
  if (selectedRow.kind === "fill") {
    const base = selectedRow.path;
    return {
      fillColour: [...base, "c"],
      fillOpacity: [...base, "o"],
      strokeColour: null,
      strokeWidth: null,
      strokeOpacity: null,
    };
  }
  if (selectedRow.kind === "stroke") {
    const base = selectedRow.path;
    return {
      fillColour: null,
      fillOpacity: null,
      strokeColour: [...base, "c"],
      strokeWidth: [...base, "w"],
      strokeOpacity: [...base, "o"],
    };
  }
  return {
    fillColour: null,
    fillOpacity: null,
    strokeColour: null,
    strokeWidth: null,
    strokeOpacity: null,
  };
}

function buildLayerKeyframeTarget(args: {
  layerAnimatedProp: unknown;
  layerKsOptions: { key: string; label: string }[];
  layerKsKey: string;
  layerKeyframeSource: "transform" | "textDoc";
  textDocumentK: unknown;
}): KeyframeTargetInfo | null {
  const { layerAnimatedProp, layerKsOptions, layerKsKey, layerKeyframeSource, textDocumentK } = args;
  if (layerKeyframeSource === "textDoc" && textDocumentK !== undefined) {
    return { label: "Text · Document", prop: textDocumentK };
  }
  if (layerKeyframeSource === "transform" && layerAnimatedProp) {
    const label = layerKsOptions.find((x) => x.key === layerKsKey)?.label ?? layerKsKey;
    return { label: `Transform · ${label}`, prop: layerAnimatedProp };
  }
  return null;
}

function buildKeyframeTarget(args: {
  selectedRow: CompositionRow | null;
  layerAnimatedProp: unknown;
  layerKsOptions: { key: string; label: string }[];
  layerKsKey: string;
  layerKeyframeSource: "transform" | "textDoc";
  channelInspect: string;
  fillColourObj: unknown;
  fillOpacityObj: unknown;
  strokeColourObj: unknown;
  strokeWidthObj: unknown;
  strokeOpacityObj: unknown;
  textDocumentK: unknown;
}): KeyframeTargetInfo | null {
  const {
    selectedRow,
    layerAnimatedProp,
    layerKsOptions,
    layerKsKey,
    layerKeyframeSource,
    channelInspect,
    fillColourObj,
    fillOpacityObj,
    strokeColourObj,
    strokeWidthObj,
    strokeOpacityObj,
    textDocumentK,
  } = args;

  if (selectedRow?.kind === "layer") {
    return buildLayerKeyframeTarget({
      layerAnimatedProp,
      layerKeyframeSource,
      layerKsKey,
      layerKsOptions,
      textDocumentK,
    });
  }
  if (selectedRow?.kind === "fill") {
    const isColour = channelInspect === "c";
    return {
      label: isColour ? "Fill · Colour" : "Fill · Opacity",
      prop: isColour ? fillColourObj : fillOpacityObj,
    };
  }
  if (selectedRow?.kind === "stroke") {
    if (channelInspect === "c") {
      return { label: "Stroke · Colour", prop: strokeColourObj };
    }
    if (channelInspect === "w") {
      return { label: "Stroke · Width", prop: strokeWidthObj };
    }
    return { label: "Stroke · Opacity", prop: strokeOpacityObj };
  }
  return null;
}

export type AnimationInspectorState = ReturnType<typeof useAnimationInspector>;
